import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, useResolvedRestaurant, getDefaultRedirect } from '../../lib/tenant';
import { useTenantAccess } from '../../lib/tenant';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Store, LogIn } from 'lucide-react';
import { Skeleton } from '../../components/ui/Skeleton';
interface LoginPageProps {
  subdomainSlug?: string;
}
export function LoginPage({
  subdomainSlug
}: LoginPageProps = {}) {
  const {
    slug: pathSlug
  } = useParams<{
    slug: string;
  }>();
  const slug = subdomainSlug || pathSlug;
  const isSubdomain = !!subdomainSlug;
  const navigate = useNavigate();
  const {
    user,
    loading: authLoading,
    signIn
  } = useAuth();
  const {
    restaurant,
    loading: restLoading,
    error: restError
  } = useResolvedRestaurant(slug);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // If already logged in, check access and redirect
  const {
    access,
    loading: accessLoading
  } = useTenantAccess(user?.id, restaurant?.restaurant_id);
  // If user is already logged in and has access, redirect
  if (user && access && slug) {
    // On subdomain, admin routes don't need :slug in path
    const redirectTo = isSubdomain ? getDefaultRedirect(slug, access.role).replace(`/admin/${slug}`, '/admin') : getDefaultRedirect(slug, access.role);
    navigate(redirectTo, {
      replace: true
    });
    return null;
  }
  if (restLoading || authLoading) {
    return <div className="min-h-screen bg-brand-offwhite flex items-center justify-center" data-id="element-3450">
        <Skeleton className="w-16 h-16 rounded-xl" data-id="element-3451" />
      </div>;
  }
  if (restError || !restaurant) {
    return <div className="min-h-screen bg-brand-offwhite flex items-center justify-center p-6" data-id="element-3452">
        <div className="max-w-md w-full text-center" data-id="element-3453">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-3454">
            <Store className="w-8 h-8 text-gray-400" data-id="element-3455" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3456">
            Restaurante no encontrado
          </h1>
          <p className="text-gray-500 mb-8" data-id="element-3457">
            No pudimos encontrar un restaurante con esta dirección.
          </p>
          <a href="/" className="inline-flex items-center justify-center px-6 py-3 bg-brand-charcoal text-white font-medium rounded-xl hover:bg-gray-800 transition-colors" data-id="element-3458">
            Volver al inicio
          </a>
        </div>
      </div>;
  }
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await signIn(email, password);
      // After sign in, we need to check access for this restaurant
      // The auth state change will trigger a re-render, and the
      // useTenantAccess hook will re-query. We'll use a small delay
      // to let the auth state propagate, then navigate.
      // A more robust approach: query access directly after login.
      const {
        data: userData
      } = await (await import('../../lib/supabase')).supabase.auth.getUser();
      if (!userData.user) {
        setError('Error al obtener sesión. Intenta de nuevo.');
        return;
      }
      // Step 1: Check if user is a platform super_admin
      const {
        checkPlatformAdmin
      } = await import('../../lib/tenant');
      const isSuperAdmin = await checkPlatformAdmin(userData.user.id);
      let accessRole = 'owner';
      if (isSuperAdmin) {
        console.log('[NOIZA][login] ✅ Super admin — granting access');
      } else {
        // Step 2: Fall back to restaurant_users check
        const {
          data: accessData,
          error: accessErr
        } = await (await import('../../lib/supabase')).supabase.from('v_user_restaurant_access').select('*').eq('user_id', userData.user.id).eq('restaurant_id', restaurant.restaurant_id).eq('is_active', true).single();
        if (accessErr || !accessData) {
          setError('No tienes acceso a este restaurante. Contacta al administrador.');
          return;
        }
        accessRole = accessData.role;
      }
      const redirectTo = isSubdomain ? getDefaultRedirect(slug!, accessRole as any).replace(`/admin/${slug}`, '/admin') : getDefaultRedirect(slug!, accessRole as any);
      navigate(redirectTo, {
        replace: true
      });
    } catch (err: any) {
      console.error('[NOIZA][login] error:', err);
      if (err?.message?.includes('Invalid login credentials')) {
        setError('Correo o contraseña incorrectos.');
      } else {
        setError('Error al iniciar sesión. Intenta de nuevo.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="min-h-screen bg-brand-offwhite flex items-center justify-center p-4" data-id="element-3459">
      <div className="w-full max-w-sm" data-id="element-3460">
        {/* Header */}
        <div className="text-center mb-8" data-id="element-3461">
          <div className="w-14 h-14 bg-brand-charcoal rounded-2xl flex items-center justify-center mx-auto mb-4" data-id="element-3462">
            <Store className="w-7 h-7 text-brand-amber" data-id="element-3463" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-3464">
            {restaurant.name}
          </h1>
          <p className="text-sm text-gray-500" data-id="element-3465">
            Inicia sesión para acceder al panel
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5" data-id="element-3466">
          <div data-id="element-3467">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-3468">
              Correo electrónico
            </label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@correo.com" required autoComplete="email" className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-amber/50 focus:border-brand-amber transition-colors" data-id="element-3469" />
          </div>

          <div data-id="element-3470">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-3471">
              Contraseña
            </label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required autoComplete="current-password" className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-amber/50 focus:border-brand-amber transition-colors" data-id="element-3472" />
          </div>

          {error && <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-xl px-4 py-3" data-id="element-3473">
              {error}
            </div>}

          <button type="submit" disabled={isSubmitting || !email || !password} className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-brand-charcoal text-white font-semibold rounded-xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-3474">
            {isSubmitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-3475" /> : <>
                <LogIn className="w-4 h-4" data-id="element-3476" />
                Iniciar Sesión
              </>}
          </button>
        </form>

        <p className="text-center text-xs text-gray-400 mt-6" data-id="element-3477">
          Plataforma NOIZA · {restaurant.name}
        </p>
      </div>
    </div>;
}